"use client";

import { useState, useEffect } from "react";
import { X, Edit, Trash2, Calendar, MapPin, Users, Phone, Instagram, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/auth-context";
import type { Reservation } from "@/types/reservations";

interface ReservationDetailModalProps {
  reservation: Reservation;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  aguardando: "Aguardando Confirmação",
  confirmada: "Confirmada",
  cancelada: "Cancelada",
};

const STATUS_COLORS: Record<string, string> = {
  aguardando: "bg-secondary text-secondary-foreground",
  confirmada: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  cancelada: "bg-destructive/10 text-destructive",
};

const LOCATION_LABELS: Record<string, string> = {
  calcada: "Calçada",
  interna: "Interna",
};

function formatDateBR(dateStr: string): string {
  const iso = String(dateStr).substring(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string, timeStr: string): string {
  const iso = String(dateStr).substring(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const dayName = date.toLocaleDateString("pt-BR", { weekday: "long" });
  const dayMonth = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const time = timeStr.substring(0, 5);
  return `${dayName} ${dayMonth} às ${time}`;
}

export function ReservationDetailModal({
  reservation,
  onClose,
  onEdit,
  onDelete,
  deleting,
}: ReservationDetailModalProps) {
  const { user } = useAuth();

  useEffect(() => {
    // Lock body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      // Unlock body scroll when modal is closed
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background rounded-lg shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-6 py-4">
          <h2 className="text-lg font-semibold">Detalhes da Reserva</h2>
          <button
            onClick={onClose}
            className="rounded-md p-2 hover:bg-secondary transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-foreground">{reservation.name}</h3>
              {(reservation.status === 'aguardando' || reservation.status === 'cancelada') && (
                <div className="mt-2 flex items-center gap-2">
                  <Badge className={STATUS_COLORS[reservation.status]}>
                    {STATUS_LABELS[reservation.status]}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Calendar className="size-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Data e Hora</p>
                <p className="text-sm text-muted-foreground">{formatDateTime(reservation.date, reservation.time)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="size-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Lugares</p>
                <p className="text-sm text-muted-foreground">
                  {reservation.seats} {reservation.seats === 1 ? 'lugar' : 'lugares'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="size-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Local</p>
                <p className="text-sm text-muted-foreground">{LOCATION_LABELS[reservation.location]}</p>
              </div>
            </div>

            {reservation.phone && (
              <div className="flex items-start gap-3">
                <Phone className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Telefone</p>
                  <p className="text-sm text-muted-foreground">{reservation.phone}</p>
                </div>
              </div>
            )}

            {reservation.instagram && (
              <div className="flex items-start gap-3">
                <Instagram className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Instagram</p>
                  <p className="text-sm text-muted-foreground">{reservation.instagram}</p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-3">Checklist</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {reservation.knows_bar ? (
                  <CheckCircle2 className="size-4 text-emerald-600" />
                ) : (
                  <XCircle className="size-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${reservation.knows_bar ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Já conhece o bar
                </span>
              </div>
              <div className="flex items-center gap-2">
                {reservation.received_instructions ? (
                  <CheckCircle2 className="size-4 text-emerald-600" />
                ) : (
                  <XCircle className="size-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${reservation.received_instructions ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Recebeu orientações
                </span>
              </div>
              <div className="flex items-center gap-2">
                {reservation.bringing_cake ? (
                  <CheckCircle2 className="size-4 text-emerald-600" />
                ) : (
                  <XCircle className="size-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${reservation.bringing_cake ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Vai levar bolo
                </span>
              </div>
            </div>
          </div>

          {reservation.notes && (
            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium mb-2">Observações</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{reservation.notes}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            {user?.role === 'super' && (
              <>
                <button
                  onClick={onDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-destructive text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="size-4" />
                  {deleting ? "Excluindo..." : "Excluir"}
                </button>
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-foreground text-background hover:opacity-90 transition-opacity"
                >
                  <Edit className="size-4" />
                  Editar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
